import { useCallback, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { FileX, Gavel, IdCard, Plus, Scale } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { fetchLegalRequests, createLegalRequest, type AdminLegalRequest } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'

const KINDS = ['data', 'preservation', 'removal', 'blocking', 'emergency'] as const

/**
 * A19.01 — Huquqiy so'rovlar ro'yxati + qo'shish. GET/POST /admin/legal (backend
 * `697c6be`). Admin-only (user-endpoint yo'q) — xodim kelgan qonuniy so'rovni
 * qayd etadi va bajarilishini kuzatadi.
 */
export function LegalQueue() {
  const t = useT()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [showForm, setShowForm] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [form, setForm] = useState({ kind: 'data', authority: '', reference: '', jurisdiction: '' })
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (form.authority.trim().length < 2) return
    setBusy(true)
    try {
      await createLegalRequest({
        kind: form.kind,
        authority: form.authority.trim(),
        ...(form.reference.trim() ? { reference: form.reference.trim() } : {}),
        ...(form.jurisdiction.trim() ? { jurisdiction: form.jurisdiction.trim() } : {}),
      })
      toast(t.legal.created, { tone: 'success' })
      setForm({ kind: 'data', authority: '', reference: '', jurisdiction: '' })
      setShowForm(false)
      setReloadKey((n) => n + 1)
    } catch (e) {
      toast(t.legal.createFailed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setBusy(false)
    }
  }

  const rowActions = useMemo<RowAction<AdminLegalRequest>[]>(
    () => [{ id: 'card', label: t.users.viewCard, icon: <IdCard size={13} />, onSelect: (r) => navigate(`/legal/${r.id}`) }],
    [t, navigate],
  )

  const columns = useMemo<AdminColumn<AdminLegalRequest>[]>(
    () => [
      { key: 'id', header: t.col.id, type: 'id', width: 70 },
      { key: 'created_at', header: t.col.time, type: 'date', width: 150 },
      {
        key: 'kind',
        header: t.legal.kind,
        type: 'enum',
        width: 130,
        options: KINDS.map((k) => ({ value: k, label: t.legal[k], status: k === 'emergency' ? 'banned' : 'review' })),
      },
      { key: 'authority', header: t.legal.authority, type: 'text', minWidth: 180 },
      { key: 'reference', header: t.legal.reference, type: 'text', width: 140 },
      {
        key: 'affected_user_id',
        header: t.legal.affected,
        type: 'text',
        width: 140,
        sortable: false,
        render: (r) =>
          r.affected_user_id ? (
            <Link to={`/users/${r.affected_user_id}`} className="text-accent hover:underline">
              {r.username ? `@${r.username}` : `#${r.affected_user_id}`}
            </Link>
          ) : (
            <span className="text-tertiary">—</span>
          ),
      },
      {
        key: 'status',
        header: t.col.status,
        type: 'enum',
        width: 120,
        options: [
          { value: 'pending', label: t.mod.pending, status: 'pending' },
          { value: 'fulfilled', label: t.legal.fulfilled, status: 'active' },
          { value: 'rejected', label: t.legal.rejected, status: 'deleted' },
        ],
      },
    ],
    [t],
  )

  const fetcher = useCallback(fetchLegalRequests, [])

  return (
    <>
      <PageHeader
        title={t.legal.queue}
        screenId="A19.01"
        description={t.legal.queueNote}
        actions={
          <div className="flex items-center gap-1.5">
            <Link
              to="/legal/rejected"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <FileX size={13} />
              {t.legal.rejectedTitle}
            </Link>
            <Link
              to="/legal/transparency"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <Scale size={13} />
              {t.legal.transparencyTitle}
            </Link>
            <button
              onClick={() => setShowForm((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-md border border-accent bg-accent-subtle px-2.5 py-1 text-sm text-accent hover:bg-accent hover:text-white"
            >
              <Plus size={13} />
              {t.legal.log}
            </button>
          </div>
        }
      />
      {showForm && (
        <div className="flex flex-wrap items-end gap-2 border-b border-border-subtle bg-surface-2 p-3">
          <select
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
            className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary"
          >
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {t.legal[k]}
              </option>
            ))}
          </select>
          <input
            value={form.authority}
            onChange={(e) => setForm({ ...form, authority: e.target.value })}
            placeholder={t.legal.authority}
            className="min-w-40 flex-1 rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary"
          />
          <input
            value={form.reference}
            onChange={(e) => setForm({ ...form, reference: e.target.value })}
            placeholder={t.legal.reference}
            className="w-40 rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary"
          />
          <input
            value={form.jurisdiction}
            onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })}
            placeholder={t.legal.jurisdiction}
            className="w-36 rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary"
          />
          <button
            disabled={busy || form.authority.trim().length < 2}
            onClick={submit}
            className="rounded-md border border-accent bg-accent-subtle px-3 py-1.5 text-sm text-accent hover:bg-accent hover:text-white disabled:opacity-50"
          >
            {t.common.save}
          </button>
        </div>
      )}
      <DataTable<AdminLegalRequest>
        key={reloadKey}
        screenId="A19.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Gavel size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
